import React, { createContext, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { useOverlayDepth } from 'modules/Common/utils/useOverlayDepth';
import { OverlayHostProvider } from 'modules/Common/utils/overlayHost';
import { cn } from 'modules/Common/utils/cn';

// Mirror of CgDialog's portal-container trick: expose the Dialog.Content
// element so descendants (popovers, dropdowns) can portal *inside* the drawer
// instead of <body>, where react-remove-scroll would otherwise block wheel
// events. Only relevant when the drawer is rendered modally.
//
// Kept alongside the shared OverlayHostProvider below — see overlayHost.js.
const CgDrawerContentContext = createContext(null);

export const useCgDrawerPortalContainer = () => useContext(CgDrawerContentContext);

// For callers that pass a custom `header`: the accessible title and the close
// control stay Radix's, so a bespoke header keeps the same aria wiring and the
// same close path (onOpenChange(false)) as the default title bar.
export const CgDrawerTitle = Dialog.Title;
export const CgDrawerClose = Dialog.Close;

// Radix-based side drawer. Replaces Bootstrap's <Offcanvas /> while letting
// v2 forms inherit the same focus/keyboard handling as CgDialog.
//
// Defaults to non-modal, right-side (modal={false}, placement="right") to
// match the original Offcanvas usage where users continue interacting with
// the diagram behind it — no overlay, no scroll lock, no focus trap. Pass
// `modal` to opt in to full modal behavior (overlay + focus trap + closes on
// outside click/Escape — e.g. a mobile nav drawer, S7 fix batch 3 Q5) and
// `placement="left"` for a left-edge slide-in.
//
// `header` replaces the default title bar with a caller-rendered node; the
// caller then owns the <CgDrawerTitle> and <CgDrawerClose> inside it.
// `onEscapeKeyDown` sees Radix's Escape event first; preventDefault() keeps
// the drawer open.
// `bodyClassName` / `footerClassName` are merged over the default paddings via
// cn(), so a caller can flatten or retint them without a second body wrapper.
export default function CgDrawer({
  open,
  onOpenChange,
  title,
  titleAccessory,
  header,
  width = 750,
  modal = false,
  placement = 'right',
  children,
  footer,
  closeButton = true,
  zIndex,
  contentClassName,
  bodyClassName,
  footerClassName,
  panelProps,
  onEscapeKeyDown,
}) {
  const { t } = useTranslation('common');
  const isLeft = placement === 'left';
  // Same rung mechanism as CgDialog (phase 21): a drawer opened over a dialog,
  // or a confirm over a drawer, takes the next rung instead of tying at 1060
  // and being ordered only by DOM insertion.
  const { overlay: overlayZ, content: contentZ } = useOverlayDepth(open);
  const resolvedOverlayZ = zIndex ?? overlayZ;
  const resolvedContentZ = zIndex ? zIndex + 1 : contentZ;

  const [contentNode, setContentNode] = useState(null);
  const setContentRef = useRef((node) => setContentNode(node)).current;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={onOpenChange}
      modal={modal}
    >
      <Dialog.Portal>
        {modal ? (
          <Dialog.Overlay
            style={{ zIndex: resolvedOverlayZ }}
            className="fixed inset-0 bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          />
        ) : null}
        <Dialog.Content
          ref={setContentRef}
          onInteractOutside={(e) => {
            // Non-modal drawers should never close from background clicks —
            // users are expected to interact with the diagram behind them.
            if (!modal) e.preventDefault();
          }}
          onOpenAutoFocus={(e) => {
            // Non-modal: don't steal focus from the page on open.
            if (!modal) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            // A caller can keep the drawer open (e.preventDefault()) while
            // Escape belongs to something inside it, e.g. an inline editor.
            onEscapeKeyDown?.(e);
            if (document.querySelector('[data-cg-escape-closes], input[aria-expanded="true"]'))
              e.preventDefault();
          }}
          style={{ width, maxWidth: '100vw', zIndex: resolvedContentZ }}
          className={
            `fixed ${isLeft ? 'left-0' : 'right-0'} top-0 h-screen focus:outline-none ` +
            'data-[state=open]:animate-in data-[state=closed]:animate-out ' +
            (isLeft
              ? 'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left '
              : 'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right ') +
            'data-[state=open]:duration-200 data-[state=closed]:duration-150 ' +
            (contentClassName ?? '')
          }
        >
          <div
            {...panelProps}
            className={`bg-white shadow-xl h-full flex flex-col ${isLeft ? 'border-r' : 'border-l'} border-slate-200`}
          >
            {header ? (
              header
            ) : title ? (
              <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <Dialog.Title className="text-base font-semibold text-slate-800 m-0 truncate">
                    {title}
                  </Dialog.Title>
                  {titleAccessory ? <div className="ms-auto">{titleAccessory}</div> : null}
                </div>
                {closeButton ? (
                  <Dialog.Close
                    aria-label={t('common:actions.close', 'Close')}
                    className="text-slate-400 hover:text-slate-700 transition-colors p-1 rounded -mr-1"
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </Dialog.Close>
                ) : null}
              </div>
            ) : null}
            <CgDrawerContentContext.Provider value={contentNode}>
              <OverlayHostProvider value={contentNode}>
                <div
                  data-testid="cg-drawer-body"
                  className={cn(
                    'overflow-auto px-4 py-4 flex-1 min-h-0 custom-scrollbar',
                    bodyClassName,
                  )}
                >
                  {children}
                </div>
                {footer ? (
                  <div
                    className={cn(
                      'border-t border-slate-200 px-4 py-3 bg-slate-50/50',
                      footerClassName,
                    )}
                  >
                    {footer}
                  </div>
                ) : null}
              </OverlayHostProvider>
            </CgDrawerContentContext.Provider>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
